import {
  faEye,
  faEyeSlash,
  faKey,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from 'components/base/Button';
import { LoginSchema } from 'pages/pages/authentication/card/model';
import AuthSocialButtons from 'components/common/AuthSocialButtons';
import { loginSchema } from 'pages/pages/authentication/card/model';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { loginUser } from 'api/auth/_requests';
import { useDispatch } from 'react-redux';
import { setUser } from 'redux-state/auth/authSlice';
import { setItemToStore, PF } from 'helpers/utils';
import { getWishlist } from 'api/user/_requests';
import { setWishlist } from 'redux-state/user/wishlistSlice';
import { getCart } from 'api/cart/_requests';
import { setCart } from 'redux-state/user/cartSlice';
import { getCustomerProfile } from 'api/user/customer_requests';
import { setCustomerDetails } from 'redux-state/user/customerSlice';
import { getVendorDetails } from 'api/user/vendor_requests';
import { setVendorDetails } from 'redux-state/user/vendorSlice';
import toast from 'react-hot-toast';
import { useState } from 'react';

const SignInForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema)
  });
  const [showPwd, setShowPwd] = useState(false);

  const onSubmit = async (data: LoginSchema) => {
    await loginUser(data)
      .then(async res => {
        dispatch(setUser(res));
        setItemToStore('user', JSON.stringify(res));

        if (res.role === 'customer') {
          const customerDetails = await getCustomerProfile();
          dispatch(setCustomerDetails(customerDetails));

          const cart = await getCart();

          const temp = cart.map(
            (item: {
              vendorId: string;
              products: {
                productId: {
                  _id: string;
                  name: string;
                  thumbnail: string;
                };
                variantId: {
                  _id: string;
                  color?: string;
                  size?: string;
                  weight?: string;
                  price: number;
                  discountedPrice: number;
                };
                quantity: number;
              }[];
              totalAmount: number;
            }) => ({
              vendorId: item.vendorId,
              products: item.products.map(
                (product: {
                  productId: {
                    _id: string;
                    name: string;
                    thumbnail: string;
                  };
                  variantId: {
                    _id: string;
                    color?: string;
                    size?: string;
                    weight?: string;
                    price: number;
                    discountedPrice: number;
                  };
                  quantity: number;
                }) => ({
                  productId: product.productId._id,
                  variantId: product.variantId._id,
                  price: product.variantId.discountedPrice,
                  quantity: product.quantity,
                  name: product.productId.name,
                  color: product.variantId.color,
                  size: product.variantId.size,
                  discountedPrice: product.variantId.discountedPrice,
                  weight: product.variantId.weight,
                  thumbnail:
                    PF +
                    (product.productId.thumbnail !== undefined
                      ? product.productId.thumbnail
                      : 'goods'),
                  vendorId: item.vendorId
                })
              ),
              totalAmount: item.totalAmount
            })
          );
          dispatch(setCart(temp));

          const wishlist = await getWishlist();
          dispatch(setWishlist(wishlist));
        } else {
          const vendorDetails = await getVendorDetails();
          dispatch(setVendorDetails(vendorDetails));
        }

        navigate('/');
      })
      .catch((err: Error) => {
        toast.error(err.message);
      });
  };

  return (
    <>
      <div className="text-center mb-7">
        <h3 className="text-body-highlight">Sign In</h3>
        <p className="text-body-tertiary">Get access to your account</p>
      </div>
      <AuthSocialButtons title="Sign in" />
      <div className="position-relative">
        <hr className="bg-body-secondary mt-5 mb-4" />
        <div className="divider-content-center">or use email</div>
      </div>
      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="email">Email address</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            id="email"
            type="email"
            className="form-icon-input"
            placeholder="name@example.com"
            {...register('email')}
            isInvalid={!!errors.email}
          />
          <FontAwesomeIcon icon={faUser} className="text-body fs-9 form-icon" />
        </div>
        {errors.email && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {errors.email.message}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      <Form.Group className="mb-3 text-start">
        <Form.Label htmlFor="password">Password</Form.Label>
        <div className="form-icon-container">
          <Form.Control
            id="password"
            type={showPwd ? 'text' : 'password'}
            className="form-icon-input"
            placeholder="Password"
            {...register('password')}
            isInvalid={!!errors.password}
          />
          <FontAwesomeIcon icon={faKey} className="text-body fs-9 form-icon" />
          <Button
            type="button"
            variant="link"
            className="position-absolute top-50 end-0 translate-middle-y me-2 p-0"
            onClick={() => setShowPwd(p => !p)}
            tabIndex={-1} /* remove if you want it in the tab order */
          >
            <FontAwesomeIcon
              icon={showPwd ? faEyeSlash : faEye}
              className="fs-8 text-body"
            />
          </Button>
        </div>
        {errors.password && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {errors.password.message}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      <Row className="flex-between-center mb-7">
        <Col xs="auto">
          <Form.Check type="checkbox" className="mb-0">
            <Form.Check.Input
              type="checkbox"
              name="remember-me"
              id="remember-me"
              defaultChecked
            />
            <Form.Check.Label htmlFor="remember-me" className="mb-0">
              Remember me
            </Form.Check.Label>
          </Form.Check>
        </Col>
        <Col xs="auto">
          <Link
            to={`/pages/authentication/${layout}/forgot-password`}
            className="fs-9 fw-semibold"
          >
            Forgot Password?
          </Link>
        </Col>
      </Row>
      <Button
        type="submit"
        variant="primary"
        className="w-100 mb-3"
        disabled={!!errors.email || !!errors.password}
        onClick={handleSubmit(onSubmit)}
      >
        Sign In
      </Button>
      <div className="text-center">
        <Link
          to={`/pages/authentication/${layout}/sign-up`}
          className="fs-9 fw-bold"
        >
          Create an account
        </Link>
      </div>
    </>
  );
};

export default SignInForm;
